import {
  BoxGeometry,
  Color,
  Material,
  Mesh,
  MeshBasicMaterial,
  Object3D,
} from "three";
import type { Engine } from "../../engine/Engine";
import type { SugarModel } from "../../loader/SugarModel";
import { SugarModelGroup } from "../../loader/SugarModelGroup";
import { logger } from "../../util/Logger";
import type { CONFIGURATOR_MODEL_TYPE } from "../../core/configurator/ConfiguratorData";
import type {
  ConfiguratorDragPreviewPhase,
  ConfiguratorDragPreviewState,
} from "./ConfiguratorMessageContracts";
import { readItemDragPayload } from "../../core/drag/itemDragSource";
import {
  clearConfiguratorPendingDragModel,
  takeConfiguratorPendingDragModel,
} from "./ConfiguratorPendingDrag";

const PREVIEW_HANDLE_KEY = "configuratorDragPreview";

interface PreviewTask {
  generation: number;
  model: SugarModelGroup;
  placeholder: Mesh;
  previewMaterial: MeshBasicMaterial;
  originalMaterials: Map<Mesh, Material | Material[]>;
  placeholderRemoved: boolean;
  loaded: boolean;
  committed: boolean;
  cancelled: boolean;
  ready: Promise<boolean>;
  resolveReady: (ready: boolean) => void;
}

export interface ConfiguratorDragPreviewPort {
  isPreview(model: SugarModel): boolean;
  commit(model: SugarModel): Promise<boolean>;
  /** Sync flag so dragend → cancelUncommitted does not undo a successful drop. */
  acknowledgeDrop(): void;
  cancel(model: SugarModel): void;
  cancelUncommitted(): SugarModel | undefined;
  cancelAll(): void;
}

/**
 * Owns the complete async drag-preview lifetime. The placeholder and model
 * identity are created synchronously; loaded geometry is attached to that same
 * model only while its generation is still current.
 */
export class ConfiguratorDragPreviewController
  implements ConfiguratorDragPreviewPort
{
  private readonly tasks = new Map<SugarModel, PreviewTask>();
  private active?: PreviewTask;
  private generation = 0;

  constructor(
    private readonly getEngine: () => Engine | undefined,
    private readonly getConfigData: () => CONFIGURATOR_MODEL_TYPE,
    private readonly onStateChange: (
      state: ConfiguratorDragPreviewState,
    ) => void,
  ) {}

  begin(event: DragEvent): SugarModel | undefined {
    const data = resolveDragModelData(event);
    if (!data) return undefined;

    this.cancelUncommitted();
    const model = new SugarModelGroup(data, this.getConfigData());
    const previewMaterial = new MeshBasicMaterial({
      color: new Color(0.5, 0.5, 0.5),
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
      toneMapped: false,
      name: "configurator-drag-preview",
    });
    const placeholder = createPlaceholder(data, previewMaterial);
    model.root.add(placeholder);
    let resolveReady!: (ready: boolean) => void;
    const ready = new Promise<boolean>((resolve) => {
      resolveReady = resolve;
    });

    const task: PreviewTask = {
      generation: ++this.generation,
      model,
      placeholder,
      previewMaterial,
      originalMaterials: new Map(),
      placeholderRemoved: false,
      loaded: false,
      committed: false,
      cancelled: false,
      ready,
      resolveReady,
    };
    model.userData[PREVIEW_HANDLE_KEY] = task;
    this.tasks.set(model, task);
    this.active = task;
    this.emit("preview", model);

    // The existing controller receives the real stable model identity now,
    // before any model/material/network promise has had a chance to resolve.
    const engine = this.getEngine();
    if (!engine) {
      this.disposeTask(task, true);
      clearConfiguratorPendingDragModel();
      logger.warn("Configurator drag preview started before engine was ready");
      return undefined;
    }
    // Consume the pending payload only after the preview is successfully owned.
    takeConfiguratorPendingDragModel();
    engine.activeScene?.add(model);
    engine.dispatchCustomEvent("model-created", { detail: model });
    engine.dispatchCustomEvent("model-added");
    this.emit("loading", model);
    void this.load(task, data);
    return model;
  }

  isPreview(model: SugarModel): boolean {
    return this.tasks.has(model);
  }

  async commit(model: SugarModel): Promise<boolean> {
    const task = this.tasks.get(model);
    if (!task || task.cancelled) return false;
    task.committed = true;
    this.restoreMaterials(task);
    if (this.active === task) this.active = undefined;
    this.emit("committed", model);
    if (task.loaded) {
      this.releaseTask(task);
      return true;
    }
    return task.ready;
  }

  acknowledgeDrop(): void {
    if (this.active && !this.active.cancelled) {
      this.active.committed = true;
    }
  }

  cancel(model: SugarModel): void {
    const task = this.tasks.get(model);
    if (task) this.disposeTask(task, true);
  }

  cancelUncommitted(): SugarModel | undefined {
    clearConfiguratorPendingDragModel();
    if (this.active && !this.active.committed) {
      const model = this.active.model;
      this.disposeTask(this.active, true);
      return model;
    }
    return undefined;
  }

  cancelAll(): void {
    for (const task of [...this.tasks.values()]) {
      this.disposeTask(task, true);
    }
  }

  private async load(task: PreviewTask, data: any): Promise<void> {
    try {
      await task.model.load(data);
      if (!this.isCurrent(task)) {
        task.model.dispose();
        return;
      }

      this.removePlaceholder(task);
      task.loaded = true;
      if (!task.committed) this.applyPreviewMaterial(task);
      this.emit(task.committed ? "committed" : "ready", task.model);
      task.resolveReady(true);
      if (task.committed) this.releaseTask(task);
    } catch (error) {
      if (!task.cancelled) {
        logger.warn("Configurator drag model load failed", error);
        this.disposeTask(task, true);
      }
    }
  }

  private isCurrent(task: PreviewTask): boolean {
    return (
      !task.cancelled &&
      this.tasks.get(task.model)?.generation === task.generation
    );
  }

  private applyPreviewMaterial(task: PreviewTask): void {
    task.model.traverse((object: Object3D) => {
      if (!(object instanceof Mesh) || object === task.placeholder) return;
      if (!task.originalMaterials.has(object)) {
        task.originalMaterials.set(object, object.material);
      }
      object.material = task.previewMaterial;
    });
  }

  private restoreMaterials(task: PreviewTask): void {
    for (const [mesh, material] of task.originalMaterials) {
      mesh.material = material;
    }
    task.originalMaterials.clear();
  }

  private disposeTask(task: PreviewTask, disposeModel: boolean): void {
    if (task.cancelled) return;
    task.cancelled = true;
    this.restoreMaterials(task);
    this.removePlaceholder(task);
    this.tasks.delete(task.model);
    delete task.model.userData[PREVIEW_HANDLE_KEY];
    if (this.active === task) this.active = undefined;
    task.previewMaterial.dispose();
    if (disposeModel) task.model.dispose();
    task.resolveReady(false);
    this.emit("cancelled", task.model);
  }

  private releaseTask(task: PreviewTask): void {
    this.tasks.delete(task.model);
    delete task.model.userData[PREVIEW_HANDLE_KEY];
    task.previewMaterial.dispose();
  }

  private removePlaceholder(task: PreviewTask): void {
    if (task.placeholderRemoved) return;
    task.placeholderRemoved = true;
    task.placeholder.removeFromParent();
    task.placeholder.geometry.dispose();
  }

  private emit(
    phase: ConfiguratorDragPreviewPhase,
    model?: SugarModel,
  ): void {
    this.onStateChange({ phase, model });
  }
}

function resolveDragModelData(event: DragEvent): any | undefined {
  // Ortak okuyucu: önce dataTransfer, olmazsa aynı doküman yedeği.
  const data = readItemDragPayload(event)?.data;
  return data && typeof data === "object" ? data : undefined;
}

function createPlaceholder(
  data: Record<string, unknown>,
  material: MeshBasicMaterial,
): Mesh {
  const width = modelDimension(data.x, 1);
  const height = modelDimension(data.y, 0.5);
  const depth = modelDimension(data.z, 1);
  const mesh = new Mesh(new BoxGeometry(width, height, depth), material);
  mesh.position.y = height * 0.5;
  mesh.name = "Configurator Drag Preview Placeholder";
  mesh.userData.configuratorDragPreview = true;
  return mesh;
}

function modelDimension(value: unknown, fallback: number): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? Math.max(numeric / 100, 0.05)
    : fallback;
}
